import { useState, useRef, useEffect } from 'react';

interface CommentInputPopupProps {
  position: { x: number; y: number };
  onSubmit: (content: string) => void;
  onCancel: () => void;
}

export default function CommentInputPopup({ position, onSubmit, onCancel }: CommentInputPopupProps) {
  const [content, setContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the textarea when the popup appears
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onCancel]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (content.trim()) {
      onSubmit(content.trim());
    }
  };

  // Calculate position to ensure popup stays within viewport
  const popupWidth = 320;
  const popupHeight = 150;

  // Since we're using translateX(-50%), we need to account for half the popup width
  // to prevent it from going off the left edge
  const minX = popupWidth / 2 + 20; // Half width + 20px margin
  const maxX = window.innerWidth - (popupWidth / 2) - 20; // Account for centering + margin

  const adjustedPosition = {
    x: Math.max(minX, Math.min(position.x, maxX)),
    y: Math.min(position.y, window.innerHeight - popupHeight - 20),
  };

  return (
    <div
      ref={popupRef}
      className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        transform: 'translateX(-50%)',
      }}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Enter your comment..."
          className="w-full h-20 px-3 py-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none"
        />
        
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!content.trim()}
            className="px-4 py-2 text-sm text-white bg-black rounded-md hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Comment
          </button>
        </div>
      </form>
    </div>
  );
}